import { Pencil, Trash } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { SelectBox } from 'src/components/SelectBox';
import { SideDropActions } from 'src/components/SideDropActions';
import Table from 'src/components/Table';
import { useFirebaseAuth } from 'src/contexts/AuthContext/FirebaseAuthContext';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import { logoutHelper } from 'src/helpers/logoutHelper';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import useOrganization from 'src/hooks/useOrganization';
import { Can, can, Subject } from 'src/hooks/usePermissions';
import { Role } from 'src/interfaces/role.interface';
import { User } from 'src/interfaces/user.interface';
import { isValidEmail } from 'src/utils/email';
import { getHttpStatus } from 'src/utils/requestUtils';

const userHeaders = [
  { key: 'name', label: 'User', width: '40%', disableSorting: true },
  { key: 'roles', label: 'Role', width: '20%', disableSorting: true },
  { key: 'email', label: 'Email', width: '20%', disableSorting: true },
  { key: 'actions', label: '', width: '20%', disableSorting: true },
];

export const Users = () => {
  const { get, post, remove, put } = useAuthedRequest();
  const notification = useNotification();
  const auth = useFirebaseAuth();
  const { organization } = useOrganization();

  const [modalInviteUserOpen, setModalInviteUserOpen] = useState(false);
  const [emailInvite, setEmailInvite] = useState('');
  const [roleInvite, setRoleInvite] = useState('');
  const [roleUpdate, setRoleUpdate] = useState('');

  const [modalEditRoleOpen, setModalEditRoleOpen] = useState(false);
  const [selectedEditRoleUser, setSelectedEditRoleUser] = useState<User | null>(
    null,
  );

  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesSelect, setRolesSelect] = useState<
    { label: string; value: string }[]
  >([]);
  const [organizationRoles, setOrganizationRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [, setUserSortConfig] = useState<{
    key: string;
    direction: string;
  } | null>(null);

  const [selectedRemoveUser, setSelectedRemoveUser] = useState<User | null>(
    null,
  );
  const [showRemoveUserModal, setShowRemoveUserModal] = useState(false);
  const [isCurrentUser, setIsCurrentUser] = useState(false);
  const [deleteWord, setDeleteWord] = useState('');
  const [currentDeleteWord, setCurrentDeleteWord] = useState('');

  const { role: currentRole } = useEnvironment();

  const fetchOrganizationUsers = async (page: number, perPage: number) => {
    if (!organization.id) {
      return;
    }

    await fetchRoles();

    setLoading(true);

    const query = new URLSearchParams({
      page: String(page),
      size: String(perPage),
    });

    const data = await get(
      `/organizations/${organization.id}/users?${query.toString()}`,
    );

    setUsers(data);
    setTotalUsers(data.length);
    setLoading(false);
  };

  useEffect(() => {
    fetchOrganizationUsers(currentPage, pageSize);
  }, [currentPage, organization.id, pageSize]);

  const handleAddUser = async () => {
    const currentUser = users.find((user) => user.authId === auth?.user?.uid);

    if (currentUser?.organization?.planId) {
      const plan = await get(`/plans/${currentUser?.organization?.planId}`);
      const maxUsers = plan?.featureGates?.user;
      if (maxUsers && users.length >= maxUsers) {
        notification.error(
          `The ${plan.name} plan is limited to ${maxUsers} user(s). Please upgrade to add more users`,
        );
        return;
      }
    }
    setModalInviteUserOpen(true);
  };

  const handleRemoveUser = (user: User, isCurrentUser: boolean = false) => {
    setSelectedRemoveUser(user);
    setShowRemoveUserModal(true);
    setIsCurrentUser(isCurrentUser);
    setDeleteWord(randomWord());
  };

  const confirmDelete = async () => {
    setLoading(true);

    if (!selectedRemoveUser) {
      setLoading(false);
      return;
    }

    const organizationId = organization.id;

    if (!organizationId) {
      notification.error('Organization ID not found');
      setLoading(false);
      return;
    }

    try {
      // Remove user
      if (isCurrentUser) {
        await remove(`/users/me`);
      } else {
        await remove(
          `/organizations/${organizationId}/users/${selectedRemoveUser.id}`,
        );
      }

      // Logout if current user
      if (isCurrentUser) {
        logoutHelper();
      }

      notification.success('User removed successfully');
      setShowRemoveUserModal(false);
      const updatedUsers = users.filter(
        (user) => user.id !== selectedRemoveUser.id,
      );
      setUsers(updatedUsers);
    } catch {
      notification.error('Failed to remove user');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    if (!organization.id) {
      return;
    }

    try {
      const data = await get('/roles');

      const organizationId = organization.id;

      const filteredRoles = organizationId
        ? data.filter((role: Role) => role.organizationId === organizationId)
        : data;

      setOrganizationRoles(filteredRoles.map((role: Role) => role.id));

      setRolesSelect(
        filteredRoles.map((role: Role) => ({
          label: role.name,
          value: role.id,
        })),
      );
      setRoles(filteredRoles);
    } catch (error) {
      notification.error('Failed to fetch roles');
      console.error('Error fetching roles:', error);
    }
  };

  const inviteUser = async (email: string, roleId: string) => {
    setLoading(true);

    try {
      await post('/invitations', {
        email,
        organizationId: organization.id,
        roleIds: [roleId],
      });

      notification.success('User invited successfully');
      setModalInviteUserOpen(false);
      await fetchOrganizationUsers(currentPage, pageSize);
    } catch (err: unknown) {
      const status = getHttpStatus(err);
      if (status === 409) {
        notification.info('User already part of the organization');
      } else {
        notification.error('Failed to invite user');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = (user: User) => {
    setSelectedEditRoleUser(user);
    setModalEditRoleOpen(true);
  };

  const updateUserRole = async (userId: string, roleId: string) => {
    setLoading(true);

    try {
      await put(`/users/${userId}/roles`, { roleIds: [roleId] });
      notification.success('User role updated successfully');
      setModalEditRoleOpen(false);
      const updatedUsers = users.map((user) => {
        if (user.id === userId) {
          return {
            ...user,
            roles: roles.filter((role) => role.id === roleId),
          };
        }
        return user;
      });
      setUsers(updatedUsers);
    } catch {
      notification.error('Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const handleIsValidEmail = async (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.target.validity.valid || !isValidEmail(e.target.value)) {
      notification.error('Invalid email');
      setEmailInvite('');
    }
  };

  const randomWord = () => {
    return Math.random().toString(36).substring(2, 8);
  };

  return (
    <div>
      <div className="flex justify-between mb-4 items-center">
        <h3>Users</h3>

        <Can operation="create" subject={Subject.USERS}>
          <Button variant="contained" onClick={handleAddUser}>
            Add User
          </Button>
        </Can>
      </div>

      <Table
        headers={userHeaders}
        rows={users.map((user) => ({
          ...user,
          name: (
            <div
              className="text-bold underline cursor-pointer"
              onClick={() => handleUpdateRole(user)}
            >
              {user.fname || user.lname
                ? `${user.fname || ''} ${user.lname || ''}`
                : '(unnamed)'}{' '}
              {auth?.user?.uid === user.authId && <b>(you)</b>}
            </div>
          ),
          organization: user.organization?.name,
          roles: user.roles
            .filter((role) => organizationRoles.includes(role.id))
            .map((role) => role?.name)
            .join(', '),
          actions: (
            <>
              {auth?.user?.uid !== user.authId && (
                <div className="flex justify-center items-center">
                  <SideDropActions
                    options={[
                      {
                        label: 'Edit',
                        Icon: Pencil,
                        onClick: () => handleUpdateRole(user),
                        show: can(currentRole, 'update', Subject.USERS),
                      },
                      {
                        label: 'Delete',
                        Icon: Trash,
                        color: 'error',
                        onClick: () => handleRemoveUser(user),
                        show: can(currentRole, 'delete', Subject.USERS),
                      },
                    ]}
                  />
                </div>
              )}

              {auth?.user?.uid === user.authId && (
                <div className="flex justify-center items-center">
                  <SideDropActions
                    options={[
                      {
                        label: 'Delete',
                        Icon: Trash,
                        color: 'error',
                        onClick: () => handleRemoveUser(user, true),
                      },
                    ]}
                  />
                </div>
              )}
            </>
          ),
        }))}
        totalItems={totalUsers}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        loading={loading}
        setSize={setPageSize}
        defaultSize={pageSize}
        onSort={setUserSortConfig}
      />

      {/* Modal to remove user */}
      <Modal
        title={`Remove User`}
        isOpen={showRemoveUserModal}
        onClose={() => setShowRemoveUserModal(false)}
        actionButton={
          <Button
            onClick={() => confirmDelete()}
            disabled={loading || currentDeleteWord !== deleteWord}
          >
            Remove User
          </Button>
        }
      >
        <div>
          You are about to remove the user{' '}
          <b>
            {selectedRemoveUser?.fname} {selectedRemoveUser?.lname}
            {!selectedRemoveUser?.fname && !selectedRemoveUser?.lname
              ? selectedRemoveUser?.email
              : ''}
          </b>{' '}
          from this environment. Are you sure you want to proceed?
          <div className="flex justify-end mt-6">
            <Input
              label={
                <>
                  type <b>{deleteWord}</b> if you want to delete the user
                </>
              }
              type="text"
              value={currentDeleteWord}
              onChange={(e) => setCurrentDeleteWord(e.target.value)}
              onBlur={() => {
                if (currentDeleteWord !== deleteWord) {
                  notification.error(
                    `Delete confirmation doesn't match. Please try again.`,
                  );
                }
              }}
            />
          </div>
          {isCurrentUser && (
            <div className="text-sm text-error italic mt-2">
              Warning: You are removing yourself from this environment.
            </div>
          )}
          {users.length === 1 && (
            <div className="text-sm text-error italic mt-2">
              Warning: Since this is the last user with this organization as
              their default organization, removing them will also delete the
              organization.
            </div>
          )}
        </div>
      </Modal>

      {/* Modal to invite user */}
      <Modal
        title={`Invite User`}
        isOpen={modalInviteUserOpen}
        onClose={() => setModalInviteUserOpen(false)}
        actionButton={
          <Button
            onClick={() => inviteUser(emailInvite, roleInvite)}
            disabled={
              loading ||
              emailInvite.length === 0 ||
              roleInvite.length === 0 ||
              !isValidEmail(emailInvite)
            }
          >
            Invite User
          </Button>
        }
      >
        <div className="flex justify-end italic text-sm">* Required fields</div>

        <div className="grid grid-cols-1 gap-6">
          <div>
            <Input
              label="Email"
              type="email"
              value={emailInvite}
              onChange={(e) => setEmailInvite(e.target.value)}
              onBlur={handleIsValidEmail}
              required
            />
          </div>

          <div>
            <Label>Select Role</Label>
            <SelectBox
              key={roles.length}
              options={rolesSelect}
              variant="outlined"
              color="primary"
              size="medium"
              onChange={(value) => setRoleInvite(value)}
              defaultValue={rolesSelect.find(
                (role) => role.value === roleInvite,
              )}
              disabled={loading}
            />
          </div>
        </div>
      </Modal>

      {/* Modal to edit role */}
      <Modal
        title={`Edit Role - ${selectedEditRoleUser?.fname || ''} ${selectedEditRoleUser?.lname || ''} ${!selectedEditRoleUser?.fname && !selectedEditRoleUser?.lname ? selectedEditRoleUser?.email : ''}`}
        isOpen={modalEditRoleOpen}
        onClose={() => setModalEditRoleOpen(false)}
        actionButton={
          <Button
            onClick={() =>
              updateUserRole(selectedEditRoleUser?.id || '', roleUpdate)
            }
            disabled={loading}
          >
            Update Role
          </Button>
        }
      >
        <div className="grid grid-cols-1 gap-6">
          <div>
            <Label>Select Role</Label>
            <SelectBox
              key={selectedEditRoleUser?.roles[0]?.id}
              options={rolesSelect}
              variant="outlined"
              color="primary"
              size="medium"
              onChange={(value) => setRoleUpdate(value)}
              defaultValue={rolesSelect.find(
                (role) => role.value === selectedEditRoleUser?.roles[0]?.id,
              )}
              disabled={loading}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};
